import { Node, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';

export const ValueRowLabel = Node.create({
  name: 'valueRowLabel',
  content: 'inline*',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'span[data-type="value-row-label"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'value-row-label' }), 0];
  },

  addNodeView() {
    return () => {
      const dom = document.createElement('span');
      dom.className = 'tt-vr-lbl';
      const contentDOM = document.createElement('span');
      contentDOM.className = 'tt-vr-lbl-inner';
      dom.appendChild(contentDOM);
      return { dom, contentDOM };
    };
  },
});

export const ValueRowValue = Node.create({
  name: 'valueRowValue',
  content: 'inline*',
  defining: true,
  isolating: true,

  parseHTML() {
    return [{ tag: 'span[data-type="value-row-value"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { 'data-type': 'value-row-value' }), 0];
  },

  addNodeView() {
    return () => {
      const dom = document.createElement('span');
      dom.className = 'tt-vr-val';
      const contentDOM = document.createElement('span');
      contentDOM.className = 'tt-vr-val-inner';
      dom.appendChild(contentDOM);
      return { dom, contentDOM };
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('valueRowValueBold'),
        appendTransaction(_, __, newState) {
          const { $from, empty } = newState.selection;
          if (!empty) return null;
          if ($from.parent.type.name !== 'valueRowValue') return null;
          if ($from.parent.content.size > 0) return null;
          const bold = newState.schema.marks.bold;
          if (!bold) return null;
          const stored = newState.storedMarks || $from.marks();
          if (stored.some(m => m.type === bold)) return null;
          return newState.tr.addStoredMark(bold.create());
        },
      }),
    ];
  },
});

const EMPTY_VR = {
  type: 'valueRow',
  content: [
    { type: 'valueRowLabel' },
    { type: 'valueRowValue' },
  ],
};

export const ValueRow = Node.create({
  name: 'valueRow',
  group: 'block',
  content: 'valueRowLabel valueRowValue',
  defining: true,

  parseHTML() {
    return [{ tag: 'div[data-type="value-row"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'value-row' }), 0];
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div');
      dom.className = 'tt-vr';
      if (node.attrs.indent) dom.style.marginLeft = `${node.attrs.indent * 24}px`;
      return {
        dom,
        contentDOM: dom,
        update(updatedNode) {
          if (updatedNode.type.name !== 'valueRow') return false;
          dom.style.marginLeft = updatedNode.attrs.indent ? `${updatedNode.attrs.indent * 24}px` : '';
          return true;
        },
      };
    };
  },

  addCommands() {
    return {
      insertValueRow: () => ({ state, chain }) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name !== 'paragraph') continue;
          const para = $from.node(d);
          const pos = $from.before(d);
          const endPos = $from.after(d);
          const vrContent = {
            type: 'valueRow',
            content: [
              { type: 'valueRowLabel', content: para.content.size > 0 ? para.content.toJSON() : undefined },
              { type: 'valueRowValue' },
            ],
          };
          return chain().deleteRange({ from: pos, to: endPos }).insertContentAt(pos, vrContent).focus().run();
        }
        return chain().insertContent(EMPTY_VR).run();
      },
      removeValueRow: () => ({ editor, state, chain }) => {
        const { $from } = state.selection;
        for (let d = $from.depth; d > 0; d--) {
          if ($from.node(d).type.name !== 'valueRow') continue;
          const vr = $from.node(d);
          const lbl = vr.child(0);
          const val = vr.child(1);
          const pos = $from.before(d);
          const endPos = $from.after(d);
          const content = [];
          if (lbl.content.size > 0) content.push({ type: 'paragraph', content: lbl.content.toJSON() });
          if (val.content.size > 0) content.push({ type: 'paragraph', content: val.content.toJSON() });
          if (content.length === 0) content.push({ type: 'paragraph' });
          return chain().deleteRange({ from: pos, to: endPos }).insertContentAt(pos, content).focus().run();
        }
        return false;
      },
      toggleValueRow: () => ({ editor, commands }) => {
        if (editor.isActive('valueRow')) return commands.removeValueRow();
        return commands.insertValueRow();
      },
    };
  },

  addKeyboardShortcuts() {
    const isInside = (editor, nodeType) => {
      const { $from } = editor.state.selection;
      for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === nodeType) return d;
      }
      return null;
    };

    return {
      'Backspace': ({ editor }) => {
        const d = isInside(editor, 'valueRow');
        if (d === null) return false;
        const { $from } = editor.state.selection;
        const vr = $from.node(d);
        const lblEmpty = vr.child(0).content.size === 0;
        const valEmpty = vr.child(1).content.size === 0;
        if (!lblEmpty || !valEmpty) return false;
        const pos = $from.before(d);
        const endPos = $from.after(d);
        editor.chain().deleteRange({ from: pos, to: endPos }).focus().run();
        return true;
      },

      'Enter': ({ editor }) => {
        const d = isInside(editor, 'valueRow');
        if (d === null) return false;
        const { $from, empty } = editor.state.selection;
        const vr = $from.node(d);
        const lblEmpty = vr.child(0).content.size === 0;
        const valEmpty = vr.child(1).content.size === 0;

        if (lblEmpty && valEmpty) {
          const pos = $from.before(d);
          const endPos = $from.after(d);
          editor.chain().deleteRange({ from: pos, to: endPos }).insertContentAt(pos, { type: 'paragraph' }).focus().run();
          return true;
        }

        const inLabel = isInside(editor, 'valueRowLabel') !== null;
        if (empty && inLabel && $from.parentOffset === 0) {
          const pos = $from.before(d);
          // Empty paragraph has total size 2 (open + close). Caret stays at
          // start of the original row's label, shifted forward by that 2 —
          // set explicitly because tiptap's insertContentAt doesn't reliably
          // remap the cursor here.
          const newCaret = $from.pos + 2;
          editor.chain().insertContentAt(pos, { type: 'paragraph' }).setTextSelection(newCaret).focus().run();
          return true;
        }

        const endPos = $from.after(d);
        editor.chain().insertContentAt(endPos, EMPTY_VR).focus(endPos + 2).run();
        return true;
      },

      'Tab': ({ editor }) => {
        const { $from } = editor.state.selection;
        const lblD = isInside(editor, 'valueRowLabel');
        if (lblD !== null) {
          const vr = $from.node(lblD - 1);
          const afterLabel = $from.after(lblD);
          const valContentEnd = afterLabel + vr.child(1).nodeSize - 1;
          editor.commands.setTextSelection(valContentEnd);
          return true;
        }
        const valD = isInside(editor, 'valueRowValue');
        if (valD !== null) {
          const vrD = valD - 1;
          const afterVr = $from.after(vrD);
          editor.commands.setTextSelection(afterVr);
          return true;
        }
        return false;
      },

      'Shift-Tab': ({ editor }) => {
        const { $from } = editor.state.selection;
        const valD = isInside(editor, 'valueRowValue');
        if (valD !== null) {
          const lblContentEnd = $from.before(valD) - 1;
          editor.commands.setTextSelection(lblContentEnd);
          return true;
        }
        const lblD = isInside(editor, 'valueRowLabel');
        if (lblD !== null) {
          const vrD = lblD - 1;
          const beforeVr = $from.before(vrD);
          editor.commands.setTextSelection(beforeVr);
          return true;
        }
        return false;
      },

      'ArrowLeft': ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty) return false;
        const valD = isInside(editor, 'valueRowValue');
        if (valD === null) return false;
        const atStart = $from.parentOffset === 0;
        if (!atStart) return false;
        const lblContentEnd = $from.before(valD) - 1;
        editor.commands.setTextSelection(lblContentEnd);
        return true;
      },

      'ArrowRight': ({ editor }) => {
        const { $from, empty } = editor.state.selection;
        if (!empty) return false;
        const lblD = isInside(editor, 'valueRowLabel');
        if (lblD === null) return false;
        const atEnd = $from.parentOffset === $from.parent.content.size;
        if (!atEnd) return false;
        const afterLabel = $from.after(lblD);
        editor.commands.setTextSelection(afterLabel + 1);
        return true;
      },
    };
  },
});

